import * as React from "react";
import mapboxgl from "mapbox-gl";
import { TripsAndRideInfo } from "./MapFloaters";
import {
    initMap,
    showTrip,
    showNeighbor,
    readyNeighborData,
    resetNeighborSourceData,
    refreshMapBounds,
} from "../../../../services/weekly/map";
import { TripsReportsRow } from "../../../../api";
import { TripDetails } from "../store";
import { showGoCancels } from "../../../../services/weekly/map";
import {
    setNeighborsErrorMsgIfExists,
    toggleNeighborsOn,
} from "../../../../services/weekly/weeklyreport";
import { Permission } from "../../../../literals";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import { colors } from "../../../../theme";

const getRideTypeCounts = (trips: TripsReportsRow[] | null) => {
    let nrCruiseRides = 0;
    let nrGoRides = 0;
    let nrTelRides = 0;
    let nrStandRides = 0;
    if (trips) {
        for (let i = 0; i < trips.length; i++) {
            const trip = trips[i];
            if (trip.boardingType == "tel") {
                nrTelRides += 1;
            }
            if (trip.boardingType == "cruising") {
                nrCruiseRides += 1;
            }
            if (trip.boardingType == "go") {
                nrGoRides += 1;
            }
            if (trip.boardingType == "stand") {
                nrStandRides += 1;
            }
        }
    }
    return { nrCruiseRides, nrGoRides, nrTelRides, nrStandRides };
};

interface MapProps {
    shiftId: string | null;
    thisMapsTrips: TripsReportsRow[] | null;
    neighbors: any;
    neighborsOn: boolean;
    tripDetails: TripDetails | null;
    permissions: Permission[];
    showNothing?: boolean;
    expandedMap: string | null;
    viewingNeighbors: boolean;
}

export class ExpandableMap extends React.Component<MapProps> {
    constructor(props: any) {
        super(props);
    }

    mapContainer;
    map;

    componentDidMount = async (): Promise<any> => {
        this.map = await initMap(this.mapContainer);
        const mapId = this.mapContainer?.id;
        if (this.props.showNothing) {
            return;
        }
        showTrip(this.map, this.props.tripDetails, this.props.thisMapsTrips);

        showGoCancels(this.map, mapId);
        if (this.props.permissions.includes("neighbors")) {
            if (this.props.expandedMap == this.props.shiftId) {
                readyNeighborData(this.map);
            } else {
                resetNeighborSourceData(this.map);
            }
        }
        refreshMapBounds(
            this.map,
            this.props.tripDetails,
            this.props.thisMapsTrips
        );
        window.scrollTo(0, 0);
    };

    // We want to not rerender map each time global state changes
    shouldComponentUpdate = (nextProps, nextState) => {
        const oldTrips = nextProps.thisMapsTrips
            ? nextProps.thisMapsTrips.length
            : 0;
        const newTrips = this.props.thisMapsTrips
            ? this.props.thisMapsTrips.length
            : 0;

        const selectedTripOld = this.props.tripDetails
            ? this.props.tripDetails.trip_id
            : "";
        const selectedTripNew = nextProps.tripDetails
            ? nextProps.tripDetails.trip_id
            : "";

        if (selectedTripNew != selectedTripOld || newTrips != oldTrips) {
            refreshMapBounds(
                this.map,
                nextProps.tripDetails,
                nextProps.thisMapsTrips
            );
            return true;
        }
        if (
            nextProps.shiftId !== this.props.shiftId ||
            oldTrips != newTrips ||
            nextProps.neighborsOn !== this.props.neighborsOn ||
            selectedTripNew != selectedTripOld
        ) {
            return true;
        }
        return false;
    };

    render = () => {
        const mapId = this.mapContainer?.id;
        showTrip(this.map, this.props.tripDetails, this.props.thisMapsTrips);
        showGoCancels(this.map, mapId);

        const {
            nrCruiseRides,
            nrGoRides,
            nrTelRides,
            nrStandRides,
        } = getRideTypeCounts(this.props.thisMapsTrips);

        if (this.props.permissions.includes("neighbors")) {
            if (this.props.expandedMap == this.props.shiftId) {
                readyNeighborData(this.map, this.props.neighbors);
            } else {
                resetNeighborSourceData(this.map);
            }
        }

        return (
            <Box id="outermost" sx={{ position: "relative" }}>
                <NeighborsSwitch
                    permissions={this.props.permissions}
                    shiftId={this.props.shiftId}
                    map={this.map}
                    viewingNeighbors={this.props.viewingNeighbors}
                    expandedMap={this.props.expandedMap}
                    mapId={mapId}
                />
                <TripsAndRideInfo
                    nr_cruise_rides={nrCruiseRides}
                    nr_go_rides={nrGoRides}
                    nr_tel_rides={nrTelRides}
                    nr_stand_rides={nrStandRides}
                    trips={this.props.thisMapsTrips}
                    tripDetails={this.props.tripDetails}
                    shiftId={this.props.shiftId}
                    map={this.map}
                />

                <Box
                    sx={{
                        height: "700px",
                        cursor: "pointer",
                        width: "100%",
                    }}
                    ref={(el) => (this.mapContainer = el)}
                    id={`${this.props.shiftId}_map`}
                ></Box>
            </Box>
        );
    };
}

const handleNeighborsToggle = (event: any, map: mapboxgl.Map) => {
    toggleNeighborsOn();
    if (event.target.checked) {
        setNeighborsErrorMsgIfExists();
    }
    showNeighbor(map, event.target.checked);
};

interface NeighborsSwitchProps {
    permissions: Permission[];
    map: any;
    shiftId: string | null;
    viewingNeighbors: boolean;
    expandedMap: any;
    mapId: any;
}
const NeighborsSwitch: React.FC<NeighborsSwitchProps> = (
    props: NeighborsSwitchProps
) => {
    if (props.permissions.includes("neighbors")) {
        if (props.shiftId == props.expandedMap) {
            showNeighbor(props.map, props.viewingNeighbors, props.shiftId);
        } else {
            showNeighbor(props.map, false);
            resetNeighborSourceData(props.map);
        }

        return (
            <Box
                sx={{
                    displayPrint: "none",
                    backgroundColor: colors.white.main,
                    position: "absolute",
                    height: "40px",
                    top: "16px",
                    right: "16px",
                    zIndex: 2000,
                    borderRadius: "4px",
                    boxShadow: "0px 1px 4px rgb(0 0 0 / 20%)",
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                    p: 2,
                    pr: 0,
                }}
            >
                <Box>他車両実績表示</Box>
                <Box>
                    <Switch
                        checked={props.viewingNeighbors}
                        onClick={(ev) => {
                            handleNeighborsToggle(ev, props.map);
                        }}
                    />
                </Box>
            </Box>
        );
    }
    return <Box></Box>;
};
